import * as React from "react";

import {
  type DirectEntityType,
  isDirectEntityType,
} from "@/shared/components/RobotoMarkdown/entityRoutes";
import {
  getDatasetLinkContext,
  getFileLinkContext,
  getMessagePathLinkContext,
  getTopicLinkContext,
  isRobotoUri,
  isTimestampLink,
  parseRobotoUri,
} from "@/shared/components/RobotoMarkdown/magicLinks.ts";
import { pickDisplayLabelString } from "@/shared/components/RobotoMarkdown/pickDisplayLabel";
import { useFormatTimestamp } from "@/shared/components/RobotoMarkdown/timeFormat";
import { Timestamp } from "@/shared/components/RobotoMarkdown/Timestamp.tsx";
import { deserializeTimestamp } from "@/shared/text";

export type DatasetLinkRenderer = (props: {
  datasetId: string;
  text: string;
  time?: bigint;
  onLinkClick?: () => void;
}) => React.ReactElement;

export type FileLinkRenderer = (props: {
  fileId: string;
  text: string;
  time?: bigint;
  onLinkClick?: () => void;
}) => React.ReactElement;

export type TopicLinkRenderer = (props: {
  topicId: string;
  text: string;
  time?: bigint;
  onLinkClick?: () => void;
}) => React.ReactElement;

export type MessagePathLinkRenderer = (props: {
  messagePathId: string;
  text: string;
  time?: bigint;
  onLinkClick?: () => void;
}) => React.ReactElement;

/**
 * Renderer for the entity types that resolve from their ID alone. One prop for
 * the whole set, since they differ only in which route they point at; the four
 * above each need a fetch or a layout decision this doesn't.
 */
export type EntityLinkRenderer = (props: {
  entityType: DirectEntityType;
  entityId: string;
  text: string;
  time?: bigint;
  onLinkClick?: () => void;
}) => React.ReactElement;

export interface RobotoMarkdownLinkProps {
  datasetLinkRenderer?: DatasetLinkRenderer;
  fileLinkRenderer?: FileLinkRenderer;
  topicLinkRenderer?: TopicLinkRenderer;
  messagePathLinkRenderer?: MessagePathLinkRenderer;
  entityLinkRenderer?: EntityLinkRenderer;
  href?: string;
  onLinkClick?: () => void;
}

const extractTextFromChildren = (children: React.ReactNode): string => {
  if (typeof children === "string") {
    return children;
  }

  if (typeof children === "number") {
    return String(children);
  }

  if (React.isValidElement(children)) {
    const element = children as React.ReactElement<{
      children?: React.ReactNode;
    }>;
    return extractTextFromChildren(element.props.children);
  }

  if (Array.isArray(children)) {
    return children.map(extractTextFromChildren).join("");
  }

  return "";
};

export const RobotoMarkdownLink = ({
  datasetLinkRenderer,
  fileLinkRenderer,
  topicLinkRenderer,
  messagePathLinkRenderer,
  entityLinkRenderer,
  onLinkClick,
  href,
  children,
}: React.PropsWithChildren<RobotoMarkdownLinkProps>) => {
  const rawChildText = extractTextFromChildren(children);
  const formatTimestampNs = useFormatTimestamp();

  // The entity renderers below take `text` as a plain string, so the label is
  // resolved here rather than inside a component.
  const resolveLabel = (timestampNs: bigint | undefined): string =>
    pickDisplayLabelString(rawChildText, timestampNs, formatTimestampNs);

  if (href) {
    try {
      if (isTimestampLink(href)) {
        const timestamp = deserializeTimestamp(href);
        return <Timestamp timestamp={timestamp}>{children}</Timestamp>;
      }

      if (datasetLinkRenderer) {
        const datasetLinkContext = getDatasetLinkContext(href);
        if (datasetLinkContext) {
          return datasetLinkRenderer({
            datasetId: datasetLinkContext.dataset.dataset_id,
            text: resolveLabel(datasetLinkContext.dataset.t),
            time: datasetLinkContext.dataset.t,
            onLinkClick: onLinkClick,
          });
        }
      }

      if (fileLinkRenderer) {
        const fileLinkContext = getFileLinkContext(href);
        if (fileLinkContext) {
          return fileLinkRenderer({
            fileId: fileLinkContext.file.file_id,
            text: resolveLabel(fileLinkContext.file.t),
            time: fileLinkContext.file.t,
            onLinkClick: onLinkClick,
          });
        }
      }

      if (topicLinkRenderer) {
        const topicLinkContext = getTopicLinkContext(href);
        if (topicLinkContext) {
          return topicLinkRenderer({
            topicId: topicLinkContext.topic.topic_id,
            text: resolveLabel(topicLinkContext.topic.t),
            time: topicLinkContext.topic.t,
            onLinkClick: onLinkClick,
          });
        }
      }

      if (messagePathLinkRenderer) {
        const messagePathLinkContext = getMessagePathLinkContext(href);
        if (messagePathLinkContext) {
          return messagePathLinkRenderer({
            messagePathId: messagePathLinkContext.message_path.message_path_id,
            text: resolveLabel(messagePathLinkContext.message_path.t),
            time: messagePathLinkContext.message_path.t,
            onLinkClick: onLinkClick,
          });
        }
      }

      if (entityLinkRenderer) {
        const entityUri = parseRobotoUri(href);
        if (entityUri && isDirectEntityType(entityUri.type)) {
          return entityLinkRenderer({
            entityType: entityUri.type,
            entityId: entityUri.id,
            text: resolveLabel(entityUri.t),
            time: entityUri.t,
            onLinkClick: onLinkClick,
          });
        }
      }
    } catch {
      // Do nothing, we'll fall back to default naive rendering behavior
    }
  }

  // A `roboto://` URI no renderer above claimed. The browser has no handler
  // for the scheme, so an anchor here is a dead click. Reachable in normal
  // use: the AI links from more entity types than this renders.
  if (href !== undefined && isRobotoUri(href)) {
    return <>{children}</>;
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      style={{ color: "var(--primary-main)", fontWeight: 500 }}
    >
      {children}
    </a>
  );
};
